import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  size,
  useClick,
  useClientPoint,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useMergeRefs,
} from '@floating-ui/react';
import { TEST_IDS, Z_INDEX } from '@va/constants';
import { TooltipOptions } from '@va/types/tooltip';
import { appHasDarkTheme } from '@va/util/helpers';
import classNames from 'classnames';
import React, { createContext, forwardRef, PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react';

type TooltipContext = ReturnType<typeof useTooltip>;

export const TooltipContext = createContext<TooltipContext>({} as TooltipContext);
const VIEWPORT_VERTICAL_PADDING = 20;

export const Tooltip: React.FC<PropsWithChildren<TooltipOptions>> = ({ children, ...rest }) => {
  const context = useTooltip(rest);

  return <TooltipContext.Provider value={context}>{children}</TooltipContext.Provider>;
};

export const TooltipTrigger = forwardRef<HTMLDivElement, React.PropsWithChildren<{}>>(({ children }, ref) => {
  const { refs, getReferenceProps } = useTooltipContext();

  // Preserve child element's ref prop value while cloning
  const childRefValue =
    React.isValidElement(children) &&
    typeof children === 'object' &&
    Object.prototype.hasOwnProperty.call(children.props, 'ref')
      ? // @ts-ignore
        children.props.ref!
      : null;
  const mergedREf = useMergeRefs([refs.setReference, ref, childRefValue]);

  if (React.isValidElement(children)) {
    return React.cloneElement(children, getReferenceProps({ ...(children.props as object), ref: mergedREf }));
  }

  return (
    <div ref={mergedREf} {...getReferenceProps()}>
      {children}
    </div>
  );
});

export const TooltipContent = forwardRef<
  HTMLDivElement,
  PropsWithChildren<
    Pick<
      TooltipOptions,
      | 'appendTo'
      | 'arrow'
      | 'arrowColor'
      | 'zIndex'
      | 'disabled'
      | 'tooltipClassNames'
      | 'useDefaultStyle'
      | 'useParentWidth'
      | 'maxWidth'
      | 'unmountOnClose'
      | 'containerId'
      | 'scrollable'
    >
  >
>(
  (
    {
      children,
      appendTo,
      arrow = true,
      arrowColor,
      zIndex = Z_INDEX.tooltipDefault,
      tooltipClassNames,
      useDefaultStyle = true,
      useParentWidth = false,
      maxWidth = 'xs:max-w-72.5 max-w-[37.5rem]',
      unmountOnClose = true,
      containerId,
      scrollable = true,
    },
    ref,
  ) => {
    const { getFloatingProps, refs, floatingStyles, context, arrowRef } = useTooltipContext();
    const isDarkTheme = appHasDarkTheme();

    const mergedRefs = useMergeRefs([refs.setFloating, ref]);

    const arrowBgColor = useMemo(() => {
      if (arrowColor) return arrowColor;
      return isDarkTheme ? '#FFFFFF' : '#000000';
    }, [arrowColor, isDarkTheme]);

    const tooltipStyles = useMemo(() => {
      if (!useDefaultStyle) return tooltipClassNames;

      return classNames(
        'p-2 rounded-md z-100 text-12 sm:mx-2 flex xs:max-h-72.5!',
        maxWidth,
        {
          'bg-black-lighter-84 text-white': !isDarkTheme,
          'bg-white-84 text-black border-solid border-gray border-2': isDarkTheme,
        },
        tooltipClassNames,
      );
    }, [useDefaultStyle, isDarkTheme, tooltipClassNames, maxWidth]);

    const getRoot = useCallback(() => {
      if (appendTo === 'parent') {
        return refs.domReference.current?.parentNode as HTMLElement;
      }

      if (appendTo) {
        return appendTo;
      }

      // Fallback to body element to support tooltips in storybook
      return (document.querySelector('#root') ?? document.querySelector('body')) as HTMLElement;
    }, [appendTo, refs.domReference]);

    if (unmountOnClose && !context.open) {
      return null;
    }

    return (
      <FloatingPortal root={getRoot()}>
        <div
          data-testid={TEST_IDS.generic.tooltipContent}
          style={{
            ...floatingStyles,
            zIndex,
            ...(useParentWidth ? { width: `${refs.domReference.current?.clientWidth}px` } : {}),
          }}
          className={classNames('wrap-break-word overflow-hidden', tooltipStyles, {
            hidden: unmountOnClose === false && !context.open,
          })}
          ref={mergedRefs}
          {...getFloatingProps()}
        >
          <div
            id={containerId}
            className={classNames('w-full', { 'overflow-auto scrollbar scrollbar-thumb': scrollable })}
            style={{ maxHeight: 'inherit', height: 'inherit' }}
          >
            {children}
          </div>
          {arrow && <FloatingArrow fill={arrowBgColor} context={context} ref={arrowRef} alignmentBaseline='auto' />}
        </div>
      </FloatingPortal>
    );
  },
);

export const useTooltip = ({
  initialOpen = false,
  open: controlledIsOpen,
  onOpenChange: onOpenChangeExternal,
  placement,
  trigger = 'default',
  interactive,
  disabled,
  anchor,
  followCursorAxis,
  outsidePress,
  ancestorScroll = true,
  offset: offsetVal = 10,
  shiftCrossAxis = false,
  offsetDependencies,
}: TooltipOptions) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);

  const isOpen = controlledIsOpen ?? uncontrolledOpen;

  const arrowRef = useRef(null);

  const data = useFloating({
    open: isOpen,
    onOpenChange: (open: boolean, event: MouseEvent) => {
      if (onOpenChangeExternal) {
        onOpenChangeExternal(open, event);
      } else {
        setUncontrolledOpen(open);
      }

      if (open && placement === 'cursor')
        data.refs.setPositionReference({
          getBoundingClientRect() {
            return {
              width: 0,
              height: 0,
              x: event.clientX,
              y: event.clientY,
              top: event.clientY,
              left: event.clientX,
              right: event.clientX,
              bottom: event.clientY,
            };
          },
        });
    },
    placement: placement === 'auto' || placement === 'cursor' ? undefined : placement,
    // See documentation for details about each middleware option
    middleware: [
      offset(offsetVal, offsetDependencies),
      flip(),
      shift({ crossAxis: shiftCrossAxis }),
      size({
        apply({ availableHeight, elements }) {
          elements.floating.style.maxHeight = `${availableHeight - VIEWPORT_VERTICAL_PADDING}px`;
        },
      }),
      arrow({ element: arrowRef, padding: 20 }),
    ],
    whileElementsMounted: autoUpdate,
    elements: {
      reference: anchor,
    },
  });

  const clientPoint = useClientPoint(data.context, {
    enabled: !!followCursorAxis,
    axis: followCursorAxis,
  });

  const click = useClick(data.context, {
    enabled: ['click'].includes(trigger) && !disabled,
    keyboardHandlers: false,
  });
  const hover = useHover(data.context, {
    handleClose: interactive ? safePolygon() : undefined,
    enabled: ['default', 'hover'].includes(trigger) && !disabled,
  });

  const dismiss = useDismiss(data.context, { ancestorScroll, outsidePress });

  const interactions = useInteractions([dismiss, hover, click, clientPoint]);

  return {
    ...data,
    ...interactions,
    arrowRef,
  };
};

export const useTooltipContext = () => {
  const context = React.useContext(TooltipContext);

  if (context == null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />');
  }

  return context;
};
